/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useState } from 'react';
import { IconButton } from '@material-ui/core';
import { CheckCircle, Delete, HighlightOff } from '@material-ui/icons';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import TextWidget from './TextWidget';
import { removeLeadingWhitespaceForValue } from '../../../shared/utils/stringUtils';
import { useAutocomplete } from '../../../redux/features/autocomplete/useAutocomplete';
import { IntegrationResourceProps } from '../../../redux/enums/IntegrationResourceProps';
import { getCurrentFieldFormKeyBySchemaId, getFormKeyBySchemaId } from '../rjsf/utils';
import AssistiveText from './AssistiveText';
import { useAutocompleteTrigger } from './useAutocompleteTrigger';
import useAlertDialog from '../../../redux/features/alertDialog/useAlertDialog';
import { getParentId, isConditionMet } from '../../../shared/validations/inputParser';
import { searchFieldById } from '../../../shared/utils/objectUtils';

export const useClasses = makeStyles(theme => ({
  actions: {
    width: '2rem',
    background: theme.palette.statusChip.seeMoreBackground,
    borderRadius: '505',
  },
  validationIcon: {
    left: '-3rem',
    position: 'relative',
    '& > svg': {
      width: '2rem',
    },
  },
  invalid: {
    color: theme.palette.statusChip.invalidBackground,
  },
  valid: {
    color: theme.palette.statusChip.validBackground,
  },
}));

const onValidate = async ({
  value,
  setValidationError,
  setValidationErrorLabel,
  validateOption,
  updateDetails,
  uiSchema,
}) => {
  const { payload } = await validateOption(value);
  // if there is no payload as detail or message we have a validation error
  if (!payload) {
    setValidationError(true);
    setValidationErrorLabel(uiSchema[uiSchemaProperties.UI_VALIDATION].autocomplete);
    updateDetails({});
    return;
  }

  if (payload.codError) {
    setValidationError(true);
    setValidationErrorLabel(payload.errorDescription);
  } else {
    setValidationError(false);
    setValidationErrorLabel(null);
  }
  updateDetails(payload);
};

async function clearFields(registry, currentSchemaKey, skipReplaceFields) {
  const formData = await registry.getFormData(`${currentSchemaKey}`);
  await clearTreeFields(formData, registry, skipReplaceFields, currentSchemaKey);
}

async function clearTreeFields(formData, registry, skipReplaceFields, currentSchemaKey) {
  const keys = Object.keys(formData);
  for (const k of keys) {
    if (typeof formData[k] === 'object') {
      await clearTreeFields(formData[k], registry, skipReplaceFields, `${currentSchemaKey}.${k}`);
    } else if (formData[k] && !skipReplaceFields.includes(k)) {
      registry.setFormData(undefined, `${currentSchemaKey}.${k}`);
    }
  }
}

const ValidatedTextWidget = (props) => {
  const { onChange, rawErrors = [], uiSchema = {}, onBlur, registry, id, formContext } = props;
  const { t } = useTranslation();
  const classes = useClasses();
  // get dynamically config 'submission.parties.X.Y.Z'
  const currentSchemaKey = getFormKeyBySchemaId(id, 2);
  const currentFieldSchemaKey = getFormKeyBySchemaId(id, 0);
  // get dynamic the current fiend 'Z'
  const currentField = getCurrentFieldFormKeyBySchemaId(id);
  const configField = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE];
  const skipReplaceFields = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_SKIP] ?? [];
  const showValidationTick = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_SHOW_VALIDATION_TICK] ?? true;
  const skipReplaceAndDialog = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_SKIP_REPLACE_DIALOG] ?? false;
  const useInitialData = uiSchema[uiSchemaProperties.UI_SHOULD_USE_INITIAL_DATA] ?? true;
  const widgetTrigger = uiSchema[uiSchemaProperties.UI_AUTOCOMPLETE_TRIGGER] ?? [];
  const defaultTriggerValue = widgetTrigger.defaultValue;
  const configuration = IntegrationResourceProps.configurations.find(c => c.name === configField);
  const initialForm = registry.getFormData(currentSchemaKey);
  const validatedTarget = `${currentSchemaKey}.fieldValidated`;

  // use to store details regarding other fields that needs to be updated
  const [details, updateDetails] = useState();

  const [shouldCheckAfterValidation, setShouldCheckAfterValidation] = React.useState(false);
  // actual value used
  const [value, setValue] = React.useState(props.value);
  // if backend validation fail
  const [validationError, setValidationError] = useState(false);
  const [validationErrorLabel, setValidationErrorLabel] = useState();
  // used to mark as dirty the field when all other schema and config validation passed
  const [readyForValidation, toggleReadyForValidation] = useState(false);

  const { validateOption } = useAutocomplete({ configuration });

  const delayedValidation = useDebouncedCallback(onValidate, 500);

  const { isOpen: isModalOpen, openAlertDialog } = useAlertDialog();

  const isWidgetActivated = () => {
    const trigger = widgetTrigger?.trigger;
    const rootLevel = widgetTrigger?.rootLevel || 1;
    if (!trigger) {
      return true;
    }
    const parentValue = searchFieldById(formContext, getParentId(id, rootLevel));

    const resetTrigger = trigger?.resetTrigger;
    if (resetTrigger && isConditionMet(resetTrigger, parentValue) && resetIfAtLeastOne(resetTrigger?.resetList)) {
      onChangeCleanData();
      clearFields(registry, currentSchemaKey, skipReplaceFields);
    }

    const triggerSkip = widgetTrigger?.trigger?.triggerSkip;
    // If skip trigger condition met, then skip trigger call
    if (triggerSkip && isConditionMet(triggerSkip, parentValue)) {
      return false;
    }

    return isConditionMet(trigger, parentValue);
  };

  const resetIfAtLeastOne = (resetList) => {
    for (const key of resetList) {
      if (registry.getFormData(`${currentSchemaKey}.${key}`)) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (useInitialData) {
      updateDetails(initialForm);
    }
  }, []);

  useEffect(() => {
    // update form fields accordingly
    onChoiceSelected();
  }, [details]);

  useEffect(() => {
    if (readyForValidation && !isModalOpen && isValidInput() && isWidgetActivated()) {
      delayedValidation({
        value,
        setValidationError,
        setValidationErrorLabel,
        validateOption,
        updateDetails,
        uiSchema,
      });
    }
  }, [readyForValidation]);

  useAutocompleteTrigger(id, uiSchema, formContext, (textWidgetVal) => {
    if (!isModalOpen && !!textWidgetVal && isWidgetActivated() && isValidInput()) {
      onValidate({
        value: textWidgetVal,
        setValidationError,
        setValidationErrorLabel,
        validateOption,
        updateDetails,
        uiSchema,
      });
    }
  });

  useEffect(() => {
    return () => {
      // clean
      setValue(null);
      updateDetails(null);
      setValidationError(false);
      setValidationErrorLabel(null);
    };
  }, []);

  const hasDetail = () => !(!details || Object.keys(details).length === 0);

  const onTypingOverValidInput = async (textValue) => {
    openAlertDialog({
      updatedTexts: {
        title: 'autocomplete.modal.title',
        message: 'autocomplete.applicant.legal.modal.message',
        confirmButton: 'autocomplete.modal.confirm',
        cancelButton: 'actions.cancel',
      },
      updatedSettings: {
        showCancelButton: true,
      },
      acceptHandler: {
        func: onConfirmReplace,
        args: textValue,
      },
    });
  };

  const _onChange = async (v) => {
    toggleReadyForValidation(false);
    const val = removeLeadingWhitespaceForValue(v);
    setValue(val);

    if (!hasDetail()) {
      await onChange(val);
    } else if (hasDetail()) {
      if (isValidInput() && !validationError && !skipReplaceAndDialog) {
        await onTypingOverValidInput(v);
      } else {
        onChangeCleanData();
        await registry.setFormData(val, `${currentFieldSchemaKey}`);
      }
    }
  };

  const onChangeCleanData = () => {
    setValidationError(false);
    setValidationErrorLabel(null);
    updateDetails(null);
    toggleReadyForValidation(false);
  };

  const isValidInput = () => {
    return !!(!rawErrors?.length && value);
  };

  useEffect(() => {
    if (rawErrors.length === 0 && shouldCheckAfterValidation) {
      if (isValidInput()) {
        toggleReadyForValidation(true);
      }
      setShouldCheckAfterValidation(false);
    }
  }, [rawErrors.length, shouldCheckAfterValidation, setShouldCheckAfterValidation]);

  const _onBlur = async (data, val) => {
    await onBlur(data, val);
    if (isValidInput()) {
      toggleReadyForValidation(true);
    }
    setShouldCheckAfterValidation(!isValidInput());
  };

  const onChoiceSelected = async () => {
    if (details && Object.keys(details).length !== 0) {
      const keys = Object.keys(details);
      for (const k of keys) {
        if (details[k]) {
          if (typeof details[k] === 'object' && details[k] !== null) {
            const subkeys = Object.keys(details[k]);
            for (const sk of subkeys) {
              await setFormDataOrDefault(details[k][sk], `${currentSchemaKey}.${k}.${sk}`);
            }
          } else {
            await setFormDataOrDefault(details[k], `${currentSchemaKey}.${k}`);
          }
        }
      }
      await registry.setFormData('true', validatedTarget);
      await registry.setFormData(value, `${currentFieldSchemaKey}`);
    } else if (details) {
      registry.setFormData(undefined, validatedTarget);
      await clearFields(registry, currentSchemaKey, skipReplaceFields);
    }
  };

  const setFormDataOrDefault = async (formValue, path) => {
    await registry.setFormData(formValue || defaultTriggerValue, path);
  };

  const onConfirmReplace = async (textValue) => {
    onChangeCleanData();
    await clearFields(registry, currentSchemaKey, skipReplaceFields);
    await registry.setFormData(textValue, `${currentFieldSchemaKey}`);
  };

  const onConfirmRemove = async () => {
    updateDetails(null);
    // create fields to skip when clearing all form
    // we should don't skip the field from where we trigger the action
    const clearAllFormFieldsToSkip = skipReplaceFields.filter(f => f !== currentField);
    setValidationError(false);
    setValidationErrorLabel(null);
    // clear all adjacent fields
    await clearFields(registry, currentSchemaKey, clearAllFormFieldsToSkip);
  };

  const onDeletingValidInput = async () => {
    openAlertDialog({
      updatedTexts: {
        title: 'autocomplete.modal.title',
        message: 'autocomplete.applicant.legal.modal.delete.message',
        confirmButton: 'actions.delete',
        cancelButton: 'actions.cancel',
      },
      updatedSettings: {
        showCancelButton: true,
      },
      acceptHandler: {
        func: onConfirmRemove,
      },
    });
  };

  const clearFieldButton = () =>
    isValidInput() ? (
      <IconButton
        className={classes.actions}
        data-testid="icon-button"
        aria-label="Remove"
        size="small"
        onClick={() => onDeletingValidInput()}
      >
        <Delete />
      </IconButton>
    ) : null;

  const isValidWidget = () =>
    showValidationTick && value ? (
      <div className={classes.validationIcon}>
        {validationError ? (
          <HighlightOff className={classes.invalid} />
        ) : (
          details && Object.keys(details).length !== 0 && <CheckCircle className={classes.valid} />
        )}
      </div>
    ) : null;

  return (
    <>
      <TextWidget
        {...props}
        hasError={rawErrors.length > 0 || validationError}
        hasWarning={validationError}
        onChange={_onChange}
        error={rawErrors.length > 0 || validationError}
        onBlur={_onBlur}
        size="medium"
        label={props?.label}
        outerInputRenderer={clearFieldButton}
        innerInputRenderer={isValidWidget}
      />
      {validationError && (
        <AssistiveText severity={uiSchema[uiSchemaProperties.UI_HELP_SEVERITY] || 'error'}>
          {t(validationErrorLabel)}
        </AssistiveText>
      )}
    </>
  );
};

export default ValidatedTextWidget;
